import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { FileUser } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { StatusBadge } from '@/components/common/Badge';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useAcademicYearPicker } from '@/hooks/usePickers';
import { useTableQuery } from '@/hooks/useTableQuery';
import { admissionsApi } from '@/services/api/endpoints/admissions';
import { gradeOfferingsApi } from '@/services/api/endpoints/academic';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type {
  AdmissionApplication,
  AdmissionApplicationInput,
  ApplicationStatus,
} from '@/types/admission';
import { formatDate, humanise } from '@/utils/format';

export const APPLICATION_STATUSES: ApplicationStatus[] = [
  'SUBMITTED',
  'UNDER_REVIEW',
  'ASSESSMENT_SCHEDULED',
  'OFFERED',
  'ACCEPTED',
  'ENROLLED',
  'REJECTED',
  'WITHDRAWN',
];

export const GENDERS = ['MALE', 'FEMALE', 'OTHER', 'UNDISCLOSED'] as const;

/**
 * The relationships the server's guardian record understands. Anything else
 * would be stored on the application and quietly become OTHER at enrolment,
 * so the form offers exactly these.
 */
export const GUARDIAN_RELATIONSHIPS = [
  'FATHER',
  'MOTHER',
  'STEPFATHER',
  'STEPMOTHER',
  'LEGAL_GUARDIAN',
  'BROTHER',
  'SISTER',
  'GRANDFATHER',
  'GRANDMOTHER',
  'UNCLE',
  'AUNT',
  'OTHER',
] as const;

/** Blank strings are "not stated", never an empty value the server must interpret. */
function optional(value: string): string | undefined {
  return value.trim() === '' ? undefined : value.trim();
}

/**
 * The intake pipeline.
 *
 * <p>An application is a family's request for a place — no student exists until
 * an accepted applicant is enrolled. Searching and filtering happen on the
 * server; the search box matches applicant, guardian and application number.
 *
 * <p>Nothing here moves an application along. Each move lives on the
 * application's own page, where the server decides which are open.
 */
export function AdmissionsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('createdAt,desc');
  const manages = can(PERMISSIONS.ADMISSION_MANAGE);

  const [status, setStatus] = useState<ApplicationStatus | ''>('');
  const [yearFilter, setYearFilter] = useState('');
  const [offeringFilter, setOfferingFilter] = useState('');
  const [adding, setAdding] = useState(false);

  const years = useAcademicYearPicker();

  const filterOfferings = useQuery({
    queryKey: queryKeys.gradeOfferings.list({ admissions: true, academicYearId: yearFilter }),
    queryFn: () =>
      gradeOfferingsApi.list({ page: 0, size: 100, academicYearId: yearFilter }),
    enabled: yearFilter !== '',
  });

  const params = {
    ...table.params,
    ...(status === '' ? {} : { status }),
    ...(yearFilter === '' ? {} : { academicYearId: yearFilter }),
    ...(offeringFilter === '' ? {} : { gradeOfferingId: offeringFilter }),
  };

  const applications = useQuery({
    queryKey: queryKeys.admissions.list(params),
    queryFn: () => admissionsApi.list(params),
  });

  const filtered = status !== '' || yearFilter !== '' || offeringFilter !== '';

  const columns: Column<AdmissionApplication>[] = [
    {
      key: 'applicant',
      header: 'Applicant',
      sortKey: 'lastName',
      render: (row) => (
        <span>
          <Link
            to={ROUTES.admissionDetail(row.id)}
            className="block text-sm font-semibold text-ink underline-offset-2 hover:text-accent hover:underline"
          >
            {row.applicantName}
          </Link>
          <span className="block font-mono text-[11px] text-subtle">{row.applicationNumber}</span>
        </span>
      ),
    },
    {
      key: 'grade',
      header: 'Applying for',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.gradeName ?? '—'}</span>
          <span className="block text-[11px] text-subtle">{row.academicYearName ?? '—'}</span>
        </span>
      ),
    },
    {
      key: 'guardian',
      header: 'Guardian',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.guardianName}</span>
          <span className="block text-[11px] text-subtle">{row.guardianPhone}</span>
        </span>
      ),
    },
    {
      key: 'received',
      header: 'Received',
      sortKey: 'createdAt',
      render: (row) => formatDate(row.createdAt),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  return (
    <>
      <ListPage
        title="Admissions"
        description="Applications from receipt to enrolment. Each one moves along on its own page."
        icon={<FileUser className="h-5 w-5" aria-hidden="true" />}
        action={manages ? <Button onClick={() => setAdding(true)}>New application</Button> : undefined}
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Applicant, guardian, application no…',
        }}
        filters={
          <>
            <FilterSelect
              label="Status"
              value={status}
              onChange={(value) => {
                setStatus(value);
                table.setPage(0);
              }}
              options={APPLICATION_STATUSES.map((value) => ({ value, label: humanise(value) }))}
            />
            <FilterSelect
              label="Year"
              value={yearFilter}
              onChange={(value) => {
                setYearFilter(value);
                // An offering belongs to one year; keeping it would filter to nothing.
                setOfferingFilter('');
                table.setPage(0);
              }}
              options={(years.data ?? []).map((row) => ({ value: row.id, label: row.name }))}
            />
            {yearFilter !== '' && (
              <FilterSelect
                label="Grade"
                value={offeringFilter}
                onChange={(value) => {
                  setOfferingFilter(value);
                  table.setPage(0);
                }}
                options={(filterOfferings.data?.data ?? []).map((row) => ({
                  value: row.id,
                  label:
                    row.campusName === undefined ? row.grade.name : `${row.grade.name} · ${row.campusName}`,
                }))}
              />
            )}
          </>
        }
        pagination={applications.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={applications.data?.data}
          rowKey={(row) => row.id}
          loading={applications.isPending}
          error={applications.error}
          onRetry={() => void applications.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle={table.searchInput === '' && !filtered ? 'No applications yet' : 'Nothing matched'}
          emptyDescription={
            table.searchInput === '' && !filtered
              ? 'Applications appear here as they are received.'
              : 'The search matches applicant, guardian and application number.'
          }
        />
      </ListPage>

      <ApplicationFormModal
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={(application) => {
          toast.success(`Application ${application.applicationNumber} received`);
          setAdding(false);
          void queryClient.invalidateQueries({ queryKey: queryKeys.admissions.all });
          navigate(ROUTES.admissionDetail(application.id));
        }}
        onError={(error) => handleApiError(error)}
      />
    </>
  );
}

/**
 * The application form, for receiving an application and for correcting one.
 *
 * <p>Shared by the list (create) and the detail page (edit). Which fields are
 * required mirrors the request's own validation so the Save button is not
 * offered for a form the server would refuse outright; everything else — a
 * birth date in the future, an offering that is closed — is the server's call,
 * and its message is shown as it is.
 */
export function ApplicationFormModal({
  open,
  application,
  onClose,
  onSaved,
  onError,
}: {
  open: boolean;
  /** Present when correcting an existing application. */
  application?: AdmissionApplication;
  onClose: () => void;
  onSaved: (application: AdmissionApplication) => void;
  onError: (error: unknown) => void;
}) {
  const years = useAcademicYearPicker();

  const [academicYearId, setAcademicYearId] = useState('');
  const [gradeOfferingId, setGradeOfferingId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [guardianName, setGuardianName] = useState('');
  const [guardianPhone, setGuardianPhone] = useState('');
  const [guardianEmail, setGuardianEmail] = useState('');
  const [guardianRelationship, setGuardianRelationship] = useState('');
  const [previousSchool, setPreviousSchool] = useState('');
  const [notes, setNotes] = useState('');

  /*
   * Reset whenever the dialog opens, from the application when editing. Done
   * during render against the previous `open` rather than in an effect so the
   * first paint of the dialog already shows the right values.
   */
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setAcademicYearId(application?.academicYearId ?? '');
      setGradeOfferingId(application?.gradeOfferingId ?? '');
      setFirstName(application?.firstName ?? '');
      setMiddleName(application?.middleName ?? '');
      setLastName(application?.lastName ?? '');
      setDateOfBirth(application?.dateOfBirth ?? '');
      setGender(application?.gender ?? '');
      setGuardianName(application?.guardianName ?? '');
      setGuardianPhone(application?.guardianPhone ?? '');
      setGuardianEmail(application?.guardianEmail ?? '');
      setGuardianRelationship(application?.guardianRelationship ?? '');
      setPreviousSchool(application?.previousSchool ?? '');
      setNotes(application?.notes ?? '');
    }
  }

  const offerings = useQuery({
    queryKey: queryKeys.gradeOfferings.list({ admissions: true, academicYearId }),
    queryFn: () => gradeOfferingsApi.list({ page: 0, size: 100, academicYearId }),
    enabled: open && academicYearId !== '',
  });

  const save = useMutation({
    mutationFn: () => {
      const payload: AdmissionApplicationInput = {
        academicYearId,
        gradeOfferingId,
        firstName: firstName.trim(),
        middleName: optional(middleName),
        lastName: lastName.trim(),
        dateOfBirth,
        gender: optional(gender),
        guardianName: guardianName.trim(),
        guardianPhone: guardianPhone.trim(),
        guardianEmail: optional(guardianEmail),
        guardianRelationship: optional(guardianRelationship),
        previousSchool: optional(previousSchool),
        notes: optional(notes),
      };
      return application === undefined
        ? admissionsApi.create(payload)
        : admissionsApi.update(application.id, payload);
    },
    onSuccess: onSaved,
    onError,
  });

  const incomplete =
    academicYearId === '' ||
    gradeOfferingId === '' ||
    firstName.trim() === '' ||
    lastName.trim() === '' ||
    dateOfBirth === '' ||
    guardianName.trim() === '' ||
    guardianPhone.trim() === '';

  // A legacy value the list does not know is still shown rather than dropped.
  const relationshipOptions: string[] = [...GUARDIAN_RELATIONSHIPS];
  if (guardianRelationship !== '' && !relationshipOptions.includes(guardianRelationship)) {
    relationshipOptions.push(guardianRelationship);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={application === undefined ? 'New application' : `Correct ${application.applicationNumber}`}
      description={
        application === undefined
          ? 'Receives an application only. The student record is created at enrolment.'
          : 'The form can be corrected until a decision is made.'
      }
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={incomplete}>
            {application === undefined ? 'Receive application' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <fieldset className="space-y-3">
          <legend className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.05em] text-ink">
            Place applied for
          </legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              id="application-year"
              label="Academic year"
              required
              value={academicYearId}
              onChange={(event) => {
                setAcademicYearId(event.target.value);
                setGradeOfferingId('');
              }}
            >
              <option value="">Select a year…</option>
              {(years.data ?? []).map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name}
                </option>
              ))}
            </SelectField>
            <SelectField
              id="application-offering"
              label="Grade"
              required
              value={gradeOfferingId}
              disabled={academicYearId === ''}
              onChange={(event) => setGradeOfferingId(event.target.value)}
              hint={
                academicYearId !== '' && !offerings.isPending && (offerings.data?.data ?? []).length === 0
                  ? 'No grades are offered in this year.'
                  : undefined
              }
            >
              <option value="">
                {academicYearId === ''
                  ? 'Choose a year first'
                  : offerings.isPending
                    ? 'Loading…'
                    : 'Select a grade…'}
              </option>
              {(offerings.data?.data ?? []).map((offering) => (
                <option key={offering.id} value={offering.id}>
                  {offering.campusName === undefined
                    ? offering.grade.name
                    : `${offering.grade.name} · ${offering.campusName}`}
                </option>
              ))}
              {/* Editing: the applied offering may no longer be listed (closed); keep it selectable. */}
              {application !== undefined &&
                gradeOfferingId === application.gradeOfferingId &&
                !(offerings.data?.data ?? []).some((row) => row.id === gradeOfferingId) && (
                  <option value={application.gradeOfferingId}>{application.gradeName ?? 'Current grade'}</option>
                )}
            </SelectField>
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.05em] text-ink">
            Applicant
          </legend>
          <div className="grid gap-3 sm:grid-cols-3">
            <TextField
              id="application-first-name"
              label="First name"
              required
              maxLength={100}
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
            <TextField
              id="application-middle-name"
              label="Middle name"
              maxLength={100}
              value={middleName}
              onChange={(event) => setMiddleName(event.target.value)}
            />
            <TextField
              id="application-last-name"
              label="Last name"
              required
              maxLength={100}
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              id="application-dob"
              label="Date of birth"
              type="date"
              required
              value={dateOfBirth}
              onChange={(event) => setDateOfBirth(event.target.value)}
            />
            <SelectField
              id="application-gender"
              label="Gender"
              value={gender}
              onChange={(event) => setGender(event.target.value)}
              hint="Needed before the applicant can be enrolled."
            >
              <option value="">Not stated yet</option>
              {GENDERS.map((value) => (
                <option key={value} value={value}>
                  {value === 'UNDISCLOSED' ? 'Prefer not to say' : humanise(value)}
                </option>
              ))}
            </SelectField>
          </div>
          <TextField
            id="application-previous-school"
            label="Previous school"
            maxLength={200}
            value={previousSchool}
            onChange={(event) => setPreviousSchool(event.target.value)}
          />
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.05em] text-ink">
            Guardian
          </legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              id="application-guardian-name"
              label="Name"
              required
              maxLength={200}
              value={guardianName}
              onChange={(event) => setGuardianName(event.target.value)}
            />
            <SelectField
              id="application-guardian-relationship"
              label="Relationship"
              value={guardianRelationship}
              onChange={(event) => setGuardianRelationship(event.target.value)}
            >
              <option value="">Not stated (legal guardian)</option>
              {relationshipOptions.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </SelectField>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              id="application-guardian-phone"
              label="Phone"
              type="tel"
              required
              maxLength={32}
              value={guardianPhone}
              onChange={(event) => setGuardianPhone(event.target.value)}
            />
            <TextField
              id="application-guardian-email"
              label="Email"
              type="email"
              maxLength={255}
              value={guardianEmail}
              onChange={(event) => setGuardianEmail(event.target.value)}
            />
          </div>
        </fieldset>

        <TextField
          id="application-notes"
          label="Notes"
          maxLength={1000}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </div>
    </Modal>
  );
}
